import type { ReactNode } from "react";
import * as DialogPrimitives from "@radix-ui/react-dialog";
import { cx } from "class-variance-authority";

import { Typography } from "../Typography/Typography";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import * as styles from "./Dialog.module.scss";

export type DialogProps = {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  hideHeader?: boolean;
  overlayClassName?: string;
  closeButtonClassName?: string;
};

const Dialog = ({
  icon,
  title,
  description,
  onClose,
  children,
  isOpen,
  className,
  hideHeader,
  overlayClassName,
  closeButtonClassName,
}: DialogProps) => {
  const hasIcon = !!icon;

  return (
    <DialogPrimitives.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay
          className={cx(styles.overlay, overlayClassName)}
        >
          <DialogPrimitives.Content className={cx(styles.content, className)}>
            {!hideHeader && (
              <div className={styles.titleAndIcon}>
                {icon && <div className={styles.icon}>{icon}</div>}
                <DialogPrimitives.Title className={styles.title}>
                  {title}
                </DialogPrimitives.Title>
              </div>
            )}

            {description && (
              <DialogPrimitives.Description asChild>
                <Typography
                  className={cx(styles.description, {
                    [styles.hasIcon]: hasIcon,
                  })}
                  variant="body"
                >
                  {description}
                </Typography>
              </DialogPrimitives.Description>
            )}

            {children}

            {!hideHeader && (
              <DialogPrimitives.Close asChild>
                <button
                  className={cx(styles.closeButton, closeButtonClassName)}
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </DialogPrimitives.Close>
            )}
          </DialogPrimitives.Content>
        </DialogPrimitives.Overlay>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Root>
  );
};

export { Dialog };
